import { Component, OnInit, Input, Output, EventEmitter, ViewChildren, QueryList} from '@angular/core';
import { FeedMetadataColumn } from '../shared/models/feed-metadata-column.model';
import { FeedColumnLookup } from '../shared/models/feed-column-lookup.model';
import { DatepickerComponent } from '../../../shared/datepicker/datepicker.component';
import { WindowService } from '../../../shared/window.service';
import { FeedGridService } from '../shared/feed-grid.service';
import { GridSelectorDetail } from '../shared/models/grid-selector-detail.model';


@Component({
    selector: '[feed-grid-header]',
    templateUrl: './feed-grid-header.component.html',
    styleUrls: ['./feed-grid-header.component.css']
})
export class FeedGridHeaderComponent implements OnInit {
    @Input() feedColumns: FeedMetadataColumn[];

    _feedColumnLookups: FeedColumnLookup[];
    get feedColumnLookups(): FeedColumnLookup[] {
        return this._feedColumnLookups;
    }

    @Input()
    set feedColumnLookups(value: FeedColumnLookup[]) {
        if (value) {
            value.forEach(lookup => {
                this.selectValues[lookup.fieldName] = 'All';
            });
            this._feedColumnLookups = value;
        }

    }
    _filters: any[] = [];
    get filters(): any[] {
        return this._filters;
    }
    @Input()
    set filters(value: any[]) {
        this._filters = value;
        this.setLookups();
    }

    @Input() outboundIsArchive = false;
    @Input() useRelativePosition = false;
    @Input() activeSortField: string;
    @Input() activeSortOrderIsDesc: boolean;


    @Output() filterChanged = new EventEmitter();
    @Output() sortChanged = new EventEmitter();

    public activeHoverColumn: any[] = [];

    public selectValues: any[] = [];
    private selectedRows: any[] = [];

    @ViewChildren(DatepickerComponent) datePickers: QueryList<DatepickerComponent>;

    constructor(private windowService: WindowService,
        private gridService: FeedGridService) {
    }

    ngOnInit() {
    }

    onMouseEnter(column: FeedMetadataColumn): void {
        if (column.enableSorting && !this.outboundIsArchive) {
            this.activeHoverColumn[column.fieldName] = true;
        }
    }

    onMouseLeave(column: FeedMetadataColumn): void {
        this.activeHoverColumn[column.fieldName] = false;
    }

    onSort(column: FeedMetadataColumn, event): void {
        const type = event.target.nodeName;
        if (column.enableSorting && type === 'SPAN' && !this.outboundIsArchive) {
            const isCurrentSortField = this.activeSortField === column.fieldName;

            this.activeSortField = column.fieldName;

            if (isCurrentSortField) {
                this.activeSortOrderIsDesc = !this.activeSortOrderIsDesc;
            } else {
                // should reset sort order to asc if sort action is not
                // the current active sort column.
                this.activeSortOrderIsDesc = false;
            }

            this.sortChanged.emit({ sortField: this.activeSortField, sortDesc: this.activeSortOrderIsDesc });
        }

    }

    onFilter(event: any): void {
        this.filterChanged.emit(this.filters);
    }

    onSelect(column: FeedMetadataColumn): void {
        this.filters[column.fieldName] = this.selectValues[column.fieldName];
        this.filterChanged.emit(this.filters);
    }

    onSelectedDateChanged(event: any): void {
        this.filters[event.fieldName] = event.value;
        this.filterChanged.emit(this.filters);
    }

    getOptions(column: FeedMetadataColumn): string[] {
        let options: string[] = [];
        if (this.feedColumnLookups !== undefined && this.feedColumnLookups.length > 0) {
            const lookupColumn = this.feedColumnLookups.find(x => x.fieldName === column.fieldName);
            options = lookupColumn.options;
        }

        return options;
    }

    closeFilters(): void {
        this.datePickers.forEach(filter => {
            filter.close();
        });
    }

    displayDatePicker(column: FeedMetadataColumn): boolean {
        return this.isFilterType(column, 'date')
            && (!column.filterUiLocation || (column.filterUiLocation && column.filterUiLocation === 'Inline'))
            && (column.dataType.toLowerCase() === 'datetime' || column.dataType.toLowerCase() === 'date');
    }

    reset(): void {
        this.filters = [];
        this.feedColumnLookups = this._feedColumnLookups;
        this.datePickers.forEach(filter => {
            filter.reset();
        });
        this.filterChanged.emit(this.filters);
    }

    displaySelector(column: FeedMetadataColumn): void {
        if (column.feedSelectorConfiguration) {
            const detail = new GridSelectorDetail();
            detail.config = column.feedSelectorConfiguration;
            detail.parent = this;
            detail.selectedRows = this.selectedRows;
            this.gridService.openSelector(detail.config.feedKey, detail);
        }
    }

    onSelectionsChanged(key: string, detail: GridSelectorDetail) {
        // detail comes from a different window.  This will remove any implicit security context
        const replacer = [detail.config.sourceIdentityField, detail.config.sourceDisplayField];
        this.selectedRows = JSON.parse(JSON.stringify(detail.selectedRows, replacer));
        // Pull out the source id, display fields from the selected rows.  put in seperate arrays for later
        const sourceIdValues = [];
        const sourceDisplayValues = [];
        if (this.selectedRows) {
            this.selectedRows.forEach(row => {
                const id = row[detail.config.sourceIdentityField];
                sourceIdValues.push(id);
                const display = row[detail.config.sourceDisplayField];
                sourceDisplayValues.push(display);
            });
        }
        // find the column that matches the target identify field and update the filters array
        let found = this.feedColumns.find(x => x.fieldName === detail.config.targetIdentityField);
        if (found && found.enableFiltering && found.feedSelectorConfiguration) {
            this.filters[detail.config.targetIdentityField] = sourceIdValues.join(';');
        }
        // find the column that matches the target display field and update the filter array
        found = this.feedColumns.find(x => x.fieldName === detail.config.targetDisplayField);
        if (found && found.enableFiltering && found.feedSelectorConfiguration) {
            this.filters[detail.config.targetDisplayField] = sourceDisplayValues.join(';');
        }
        // fire filter event
        this.filterChanged.emit(this.filters);
    }


    isFilterType(column: FeedMetadataColumn, filterType: string): boolean {
        return ((column.enableFiltering && !this.outboundIsArchive) || (column.enableArchiveFiltering && this.outboundIsArchive))
            && column.filterType
            && typeof (column.filterType) === 'string'
            && column.filterType.toLowerCase() === filterType;
    }
    isColumnSortable(column: FeedMetadataColumn): boolean {
        return column.enableSorting && !this.outboundIsArchive;
    }

    private setLookups() {
        this.feedColumns.forEach(column => {
            if (column.filterType && column.filterType.toLowerCase() === 'lookup' && this.filters[column.fieldName]) {
                this.selectValues[column.fieldName] = this.filters[column.fieldName];
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/main/feeds/feed-grid-header/feed-grid-header.component.ts